<script lang="ts">
  // ============================================================
  // Community Feed Landing Page
  // ------------------------------------------------------------
  // - Displays a list of hot posts
  // - "Newspaper" style login wall after 3 posts
  // ============================================================
  import { page } from '@inertiajs/svelte'
  import PostCard from '@/components/ui/PostCard.svelte'
  import ContentGating from '@/components/ui/ContentGating.svelte'
  export let posts: any[] = [];
  export let stats: { users_count: number; organizations_count: number };
  // 소개·FAQ 문구. 정본은 config/seo_geo/pages/home.yml — 서버 HTML 의 FAQPage JSON-LD 와 같은 값이다.
  export let seo: {
    heading: string;
    intro: string[];
    features: { title: string; description: string }[];
    faq: { question: string; answer: string }[];
  } | undefined = undefined;

  $: user = page.props.auth?.user;
  $: isVerified = page.props.auth?.verified ?? false;
  $: devAutoLoggedIn = page.props.auth?.dev_auto_logged_in ?? false;
  $: loginPath = page.props.auth?.login_path ?? '/login';
  $: logoutPath = page.props.auth?.logout_path ?? '/logout';
  $: ctaHref = user ? '/verification' : '/register';
  $: ctaText = user ? '1분 만에 인증하고 전체 보기' : '무료로 회원가입하고 전체 보기';

  let mobileMenuOpen = false;

  function toggleMobileMenu(event: MouseEvent) {
    event.stopPropagation();
    mobileMenuOpen = !mobileMenuOpen;
  }

  function closeMobileMenu() {
    mobileMenuOpen = false;
  }
</script>

<svelte:window on:click={closeMobileMenu} />

<div class="min-h-screen bg-gray-50 dark:bg-dark-primary">
  <!-- Header -->
  <nav class="sticky top-0 z-40 bg-white/95 dark:bg-dark-secondary/95 backdrop-blur-xl border-b border-gray-200/80 dark:border-dark-border/60 shadow-sm">
    <div class="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
      <!-- Left - Menu button and branding -->
      <div class="flex items-center gap-4">
        <button
          on:click={toggleMobileMenu}
          class="p-2 -ml-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-all duration-200 cursor-pointer lg:hidden group"
          aria-label="메뉴 열기"
          aria-expanded={mobileMenuOpen}
        >
          <svg
            class="h-6 w-6 text-content-secondary group-hover:text-gray-900 dark:group-hover:text-dark-text-high transition-colors duration-200"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>

        <!-- Logo (Dashboard Navigation과 동일) -->
        <a href="/" class="flex items-center gap-3">
          <img src="/favicon-32x32.png" alt="AX Hub" class="w-9 h-9 rounded-xl shadow-md" />
          <div class="hidden sm:block">
            <h1 class="text-lg font-bold text-content">AX Hub</h1>
            <p class="text-xs text-content-tertiary -mt-0.5">전문가 커뮤니티</p>
          </div>
        </a>
      </div>

      <!-- Right Actions (Desktop) -->
      <div class={`hidden sm:flex items-center gap-2 ${mobileMenuOpen ? 'sm:hidden' : ''}`}>
        {#if user}
          <a
            href="/dashboard"
            class="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-xl font-medium transition-all duration-200 shadow-sm hover:shadow-md text-sm"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
            </svg>
            대시보드
          </a>
        {:else}
          <a
            href="/login"
            class="text-sm text-content-secondary hover:text-gray-900 dark:hover:text-dark-text-high font-medium transition-colors px-4 py-2"
          >
            로그인
          </a>
          <a
            href="/register"
            class="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-xl font-medium transition-all duration-200 shadow-sm hover:shadow-md text-sm"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
            </svg>
            회원가입
          </a>
        {/if}
      </div>
    </div>

    {#if mobileMenuOpen}
      <div
        class="lg:hidden border-t border-gray-200/80 dark:border-dark-border/60 bg-white/95 dark:bg-dark-secondary/95"
        role="presentation"
        tabindex="-1"
        on:click|stopPropagation
        on:keydown|stopPropagation
      >
        <div class="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-end gap-2">
          {#if user}
            <a
              href="/dashboard"
              class="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-xl font-medium transition-all duration-200 shadow-sm hover:shadow-md text-sm"
              on:click={closeMobileMenu}
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
              </svg>
              대시보드
            </a>
          {:else}
            <a
              href="/login"
              class="text-sm text-content-secondary hover:text-gray-900 dark:hover:text-dark-text-high font-medium transition-colors px-4 py-2"
              on:click={closeMobileMenu}
            >
              로그인
            </a>
            <a
              href="/register"
              class="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-xl font-medium transition-all duration-200 shadow-sm hover:shadow-md text-sm"
              on:click={closeMobileMenu}
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
              </svg>
              회원가입
            </a>
          {/if}
        </div>
      </div>
    {/if}
  </nav>

  <!-- Feed Section -->
  <main class="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 sm:py-10">
    <!-- Title Section -->
    <div class="mb-8 sm:mb-10">
      <h1 class="text-h1 sm:text-display text-content mb-2">
        오늘의 핫 토픽
      </h1>
      <p class="text-body2 text-content-tertiary">
        <span class="font-medium text-gray-700 dark:text-dark-text-medium">{stats.users_count.toLocaleString()}명</span>의 전문가들이 보고 있는 이슈
      </p>
    </div>

    <div class="mb-8">
      {#if devAutoLoggedIn}
        <div class="rounded-3xl border border-warning/25 bg-gradient-to-r from-warning-light via-amber-50 to-white px-5 py-5 shadow-sm dark:border-warning/20 dark:bg-warning/10">
          <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div class="space-y-1.5">
              <div class="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-warning-dark dark:bg-dark-secondary/60 dark:text-warning">
                <span class="inline-block h-2 w-2 rounded-full bg-warning"></span>
                개발 모드 자동 로그인
              </div>
              <p class="text-base font-semibold text-content">
                현재는 세션 로그인 없이 테스트용 계정으로 자동 진입 중입니다.
              </p>
              <p class="text-sm text-content-secondary">
                그래서 대시보드가 바로 열리고, 로그인이나 로그아웃이 꼭 필요한 것처럼 보이지 않을 수 있습니다.
              </p>
            </div>
            <div class="flex flex-wrap gap-3">
              <a href="/dashboard" class="inline-flex items-center justify-center rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100">
                대시보드 열기
              </a>
              <a href={loginPath} class="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-dark-border dark:bg-dark-secondary dark:text-dark-text-high dark:hover:bg-dark-tertiary">
                실제 로그인 화면 보기
              </a>
            </div>
          </div>
        </div>
      {:else if user}
        <div class="rounded-3xl border border-success/20 bg-gradient-to-r from-success-light/70 to-white px-5 py-5 shadow-sm dark:border-success/20 dark:bg-success/10">
          <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div class="space-y-1.5">
              <div class="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-success dark:bg-dark-secondary/60">
                <span class="inline-block h-2 w-2 rounded-full bg-success"></span>
                로그인됨
              </div>
              <p class="text-base font-semibold text-content">
                {user.name || user.nickname || user.email} 계정으로 로그인되어 있습니다.
              </p>
              <p class="text-sm text-content-secondary">
                커뮤니티 피드를 둘러보거나 바로 내 대시보드로 이동할 수 있습니다.
              </p>
            </div>
            <div class="flex flex-wrap gap-3">
              <a href="/dashboard" class="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-dark">
                내 대시보드
              </a>
              <a href={logoutPath} class="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-dark-border dark:bg-dark-secondary dark:text-dark-text-high dark:hover:bg-dark-tertiary">
                로그아웃
              </a>
            </div>
          </div>
        </div>
      {:else}
        <div class="rounded-3xl border border-primary/15 bg-gradient-to-r from-primary-50 via-white to-white px-5 py-5 shadow-sm dark:border-primary/20 dark:bg-primary/10">
          <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div class="space-y-1.5">
              <div class="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold text-primary dark:bg-dark-secondary/60">
                <span class="inline-block h-2 w-2 rounded-full bg-primary"></span>
                로그인 전
              </div>
              <p class="text-base font-semibold text-content">
                지금은 둘러보기 모드입니다.
              </p>
              <p class="text-sm text-content-secondary">
                로그인하면 대시보드, 메시지, 알림과 전체 커뮤니티 기능을 사용할 수 있습니다.
              </p>
            </div>
            <div class="flex flex-wrap gap-3">
              <a href={loginPath} class="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-dark">
                로그인
              </a>
              <a href="/register" class="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-dark-border dark:bg-dark-secondary dark:text-dark-text-high dark:hover:bg-dark-tertiary">
                회원가입
              </a>
            </div>
          </div>
        </div>
      {/if}
    </div>

    <!-- Posts Feed -->
    <div class="space-y-4">
      {#each posts.slice(0, 3) as post (post.id)}
        <PostCard {post} href={`/posts/${post.id}`} />
      {/each}

      <!-- Gated Content Section -->
      {#if !isVerified && posts.length > 3}
        <div class="relative">
          <ContentGating
            ctaText={ctaText}
            ctaHref={ctaHref}
            memberCount={`${stats.users_count.toLocaleString()}명`}
            companies="삼성전자, 네이버"
          >
            {#snippet preview()}
              <div class="space-y-4 opacity-40 pointer-events-none select-none" aria-hidden="true" inert>
                 {#each posts.slice(3, 6) as post (post.id)}
                    <PostCard {post} href="#" />
                 {/each}
              </div>
            {/snippet}
          </ContentGating>
        </div>
      {:else if posts.length > 3}
        {#each posts.slice(3) as post (post.id)}
          <PostCard {post} href={`/posts/${post.id}`} />
        {/each}
      {/if}
    </div>

    {#if !user}
      <div class="mt-16 text-center">
        <div class="w-12 h-12 bg-primary-50 dark:bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
          </svg>
        </div>
        <p class="text-body2 text-content-tertiary mb-3">
          더 많은 AX 인사이트를 원하시나요?
        </p>
        <a href="/register" class="text-primary font-semibold hover:text-primary-dark transition-colors text-body1">
          1분만에 가입하기
          <svg class="w-4 h-4 inline-block ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </a>
      </div>
    {/if}

    {#if seo}
      <section aria-labelledby="about-axhub" class="mt-16 max-w-3xl mx-auto">
        <h2 id="about-axhub" class="text-h2 text-content mb-3 text-balance">{seo.heading}</h2>
        {#each seo.intro as line}
          <p class="text-body1 text-content-secondary text-pretty">{line}</p>
        {/each}

        <ul class="mt-6 grid gap-3 sm:grid-cols-2">
          {#each seo.features as feature}
            <li class="rounded-2xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-secondary px-4 py-3">
              <p class="text-body1 font-semibold text-content">{feature.title}</p>
              <p class="text-body2 text-content-tertiary text-pretty">{feature.description}</p>
            </li>
          {/each}
        </ul>

        <h2 class="text-h2 text-content mt-12 mb-4">자주 묻는 질문</h2>
        <dl class="divide-y divide-gray-200 dark:divide-dark-border">
          {#each seo.faq as item}
            <div class="py-4">
              <dt class="text-body1 font-semibold text-content text-balance">{item.question}</dt>
              <dd class="mt-1 text-body2 text-content-secondary text-pretty">{item.answer}</dd>
            </div>
          {/each}
        </dl>
      </section>
    {/if}
  </main>
</div>
